import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { UserPlus, KeyRound, Copy, Trash2, ShieldCheck, ScrollText, Loader2, Power, AlertTriangle } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const STATUS = { disponible: "Disponible", de_garde: "De garde", occupe: "Occupé", absent: "Absent" };
const fmt = (iso) => { try { return new Date(iso).toLocaleString("fr-FR"); } catch { return ""; } };

export function IntranetAdmin() {
  const [employees, setEmployees] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [audit, setAudit] = useState([]);
  const [esc, setEsc] = useState({ enabled: true, delay_min: 5, twilio_configured: false });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [cred, setCred] = useState(null);
  const [form, setForm] = useState({ employee_id: "", secteur: "", poste: "", identifiant: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [emp, sec, acc, aud, es] = await Promise.all([
        api.get("/admin/hr/employees"), api.get("/admin/intranet/sectors"),
        api.get("/admin/intranet/accounts"), api.get("/admin/intranet/audit?limit=100"),
        api.get("/admin/intranet/escalation"),
      ]);
      const el = Array.isArray(emp.data) ? emp.data : (emp.data.employees || []);
      setEmployees(el); setSectors(sec.data.secteurs || []);
      setAccounts(acc.data.accounts || []); setAudit(aud.data.audit || []); setEsc(es.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copié"); };

  const provision = async () => {
    if (!form.employee_id || !form.secteur) { toast.error("Collaborateur et secteur requis"); return; }
    setCreating(true); setCred(null);
    try {
      const { data } = await api.post("/admin/intranet/accounts", form);
      setCred(data); setForm({ employee_id: "", secteur: "", poste: "", identifiant: "" });
      toast.success("Compte professionnel créé"); load();
    } catch (e) { toast.error(apiError(e)); } finally { setCreating(false); }
  };

  const resetPwd = async (uid) => {
    try { const { data } = await api.post(`/admin/intranet/accounts/${uid}/reset-password`); setCred(data); toast.success("Mot de passe réinitialisé"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggleActive = async (a) => {
    try { await api.patch(`/admin/intranet/accounts/${a.user_id}`, { active: !a.active, secteur: a.intranet_secteur, poste: a.intranet_poste }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (uid) => { if (!window.confirm("Supprimer ce compte professionnel ?")) return; try { await api.delete(`/admin/intranet/accounts/${uid}`); toast.success("Supprimé"); load(); } catch (e) { toast.error(apiError(e)); } };

  const saveEsc = async () => {
    try { const { data } = await api.put("/admin/intranet/escalation", { enabled: esc.enabled, delay_min: Number(esc.delay_min) || 5, repeat_min: Number(esc.repeat_min) || 5, max_relances: Number(esc.max_relances) || 3 }); setEsc((s) => ({ ...s, ...data })); toast.success("Escalade enregistrée"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const runEsc = async () => {
    try { const { data } = await api.post("/admin/intranet/escalation/run-now"); toast.success(`Escalade : ${data.escalated || 0} alerte(s), ${data.notified || 0} notification(s)`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="intranet-admin">
      <div className="mb-4 flex items-center gap-2 rounded-xl bg-navy/5 px-4 py-3 text-navy">
        <ShieldCheck size={18} />
        <span className="text-sm font-semibold">Messagerie intranet — comptes professionnels & journal d'audit</span>
      </div>

      {/* Provisioning */}
      <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-3 flex items-center gap-2 font-display text-sm font-semibold text-navy"><UserPlus size={16} className="text-ciel" /> Créer un compte professionnel (depuis la RH)</h3>
        <div className="grid gap-3 sm:grid-cols-4">
          <div>
            <label className="text-[11px] text-slate-500">Collaborateur RH</label>
            <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
              <SelectTrigger className="mt-1 h-10 text-sm" data-testid="intranet-emp-select"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
              <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.prenoms} {e.nom} {e.poste ? `· ${e.poste}` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Secteur (canal)</label>
            <Select value={form.secteur} onValueChange={(v) => setForm({ ...form, secteur: v })}>
              <SelectTrigger className="mt-1 h-10 text-sm" data-testid="intranet-sector-select"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
              <SelectContent>{sectors.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Identifiant (email)</label>
            <Input value={form.identifiant} onChange={(e) => setForm({ ...form, identifiant: e.target.value })} placeholder="ex : awa.kone@…" className="mt-1 h-10 text-sm" data-testid="intranet-ident" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Poste (optionnel)</label>
            <Input value={form.poste} onChange={(e) => setForm({ ...form, poste: e.target.value })} placeholder="ex : Médecin urgentiste" className="mt-1 h-10 text-sm" data-testid="intranet-poste" />
          </div>
        </div>
        <Button onClick={provision} disabled={creating} data-testid="intranet-provision" className="mt-3 rounded-full bg-navy px-5 text-sm text-white hover:bg-navy-light">
          {creating ? <><Loader2 size={14} className="mr-1 animate-spin" /> Création…</> : <><UserPlus size={14} className="mr-1" /> Créer le compte</>}
        </Button>

        {cred && (
          <div className="mt-4 rounded-xl border border-emerald-300 bg-emerald-50 p-4" data-testid="intranet-cred-box">
            <p className="text-sm font-semibold text-emerald-800">Identifiants à transmettre au professionnel (affichés une seule fois)</p>
            <div className="mt-2 flex flex-wrap items-center gap-4 text-sm">
              <span>Identifiant : <b className="font-mono">{cred.identifiant}</b></span>
              <span>Mot de passe : <b className="font-mono">{cred.temp_password}</b></span>
              <Button size="sm" variant="outline" onClick={() => copy(`Identifiant: ${cred.identifiant}\nMot de passe: ${cred.temp_password}`)} className="rounded-full text-xs"><Copy size={12} className="mr-1" /> Copier</Button>
            </div>
          </div>
        )}
      </div>

      {/* Escalade code rouge */}
      <div className="mb-5 rounded-2xl border border-red-200 bg-red-50/40 p-5" data-testid="intranet-escalation">
        <h3 className="mb-2 flex items-center gap-2 font-display text-sm font-semibold text-navy"><AlertTriangle size={16} className="text-red-600" /> Escalade « code rouge » (SMS/WhatsApp)</h3>
        <p className="mb-3 text-xs text-slate-500">Si un message code rouge d'un canal secteur reste non lu après le délai, les soignants « De garde » du secteur reçoivent une alerte SMS + WhatsApp. {esc.twilio_configured ? "Twilio configuré (envoi réel)." : "Twilio non configuré en preview → envoi simulé (visible dans le journal d'audit)."}</p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!esc.enabled} onChange={(e) => setEsc({ ...esc, enabled: e.target.checked })} data-testid="intranet-esc-enabled" /> Activer l'escalade</label>
          <div>
            <label className="block text-[11px] text-slate-500">Délai initial (min)</label>
            <Input type="number" min={1} max={120} value={esc.delay_min} onChange={(e) => setEsc({ ...esc, delay_min: e.target.value })} className="mt-1 h-9 w-24 text-sm" data-testid="intranet-esc-delay" />
          </div>
          <div>
            <label className="block text-[11px] text-slate-500">Relance toutes les (min)</label>
            <Input type="number" min={1} max={120} value={esc.repeat_min} onChange={(e) => setEsc({ ...esc, repeat_min: e.target.value })} className="mt-1 h-9 w-28 text-sm" data-testid="intranet-esc-repeat" />
          </div>
          <div>
            <label className="block text-[11px] text-slate-500">Relances max</label>
            <Input type="number" min={1} max={20} value={esc.max_relances} onChange={(e) => setEsc({ ...esc, max_relances: e.target.value })} className="mt-1 h-9 w-24 text-sm" data-testid="intranet-esc-max" />
          </div>
          <Button size="sm" onClick={saveEsc} data-testid="intranet-esc-save" className="rounded-full bg-navy px-4 text-xs text-white">Enregistrer</Button>
          <Button size="sm" variant="outline" onClick={runEsc} data-testid="intranet-esc-run" className="rounded-full text-xs">Vérifier maintenant</Button>
        </div>
        <p className="mt-2 text-[11px] text-slate-400">La relance s'arrête automatiquement dès qu'un soignant « De garde » lit le message, ou après le nombre maximum de relances.</p>
      </div>

      {/* Accounts */}
      <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-3 font-display text-sm font-semibold text-navy">Comptes professionnels ({accounts.length})</h3>
        {loading ? <p className="text-sm text-slate-400"><Loader2 size={14} className="mr-1 inline animate-spin" /> Chargement…</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="intranet-accounts-table">
              <thead><tr className="border-b border-slate-100 text-left text-[11px] uppercase text-slate-400">
                <th className="py-2">Nom</th><th>Identifiant</th><th>Secteur</th><th>Poste</th><th>Dispo.</th><th>État</th><th className="text-right">Actions</th></tr></thead>
              <tbody>
                {accounts.map((a) => (
                  <tr key={a.user_id} className="border-b border-slate-50" data-testid={`intranet-acc-${a.user_id}`}>
                    <td className="py-2.5 font-medium text-slate-700">{a.name}</td>
                    <td className="font-mono text-xs text-slate-500">{a.email}</td>
                    <td className="text-slate-600">{a.intranet_secteur}</td>
                    <td className="text-slate-600">{a.intranet_poste}</td>
                    <td className="text-xs text-slate-500">{STATUS[a.intranet_status] || "—"}</td>
                    <td>{a.active === false ? <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[11px] text-slate-600">Désactivé</span> : <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] text-emerald-700">Actif</span>}</td>
                    <td className="py-2">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => resetPwd(a.user_id)} title="Réinitialiser le mot de passe" data-testid={`intranet-reset-${a.user_id}`} className="rounded-lg p-1.5 text-amber-600 hover:bg-amber-50"><KeyRound size={15} /></button>
                        <button onClick={() => toggleActive(a)} title={a.active === false ? "Réactiver" : "Désactiver"} data-testid={`intranet-toggle-${a.user_id}`} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"><Power size={15} /></button>
                        <button onClick={() => del(a.user_id)} title="Supprimer" data-testid={`intranet-del-${a.user_id}`} className="rounded-lg p-1.5 text-red-500 hover:bg-red-50"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {accounts.length === 0 && <tr><td colSpan={7} className="py-4 text-center text-slate-400">Aucun compte pour le moment.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Audit */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-1 flex items-center gap-2 font-display text-sm font-semibold text-navy"><ScrollText size={16} className="text-ciel" /> Journal d'audit (métadonnées)</h3>
        <p className="mb-3 text-xs text-slate-400">Le contenu des messages est chiffré et inaccessible. Seules les métadonnées (émetteur, canal, date, code rouge) sont journalisées.</p>
        <div className="max-h-72 overflow-y-auto">
          <table className="w-full text-sm" data-testid="intranet-audit-table">
            <thead><tr className="border-b border-slate-100 text-left text-[11px] uppercase text-slate-400"><th className="py-2">Date</th><th>Événement</th><th>Portée</th><th>Émetteur</th><th>Code rouge</th></tr></thead>
            <tbody>
              {audit.map((a) => (
                <tr key={a.id} className="border-b border-slate-50">
                  <td className="py-2 text-xs text-slate-500">{fmt(a.created_at)}</td>
                  <td className="text-slate-600">{a.event}</td>
                  <td className="text-slate-600">{a.scope === "dm" ? "Message direct" : "Canal"}</td>
                  <td className="text-slate-600">{a.actor_name}</td>
                  <td>{a.code_rouge ? <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">Oui</span> : "—"}</td>
                </tr>
              ))}
              {audit.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-slate-400">Aucun événement.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
